import { noop } from 'lodash-es';
import React from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import 'react-pdf/dist/Page/TextLayer.css';

import defaultHeaderImg from 'images/BDLogotypeTransparentBKGfinal.png';
import { FjCheckbox, FjModal } from '../primitives';

import * as Components from './components';

// react-pdf 8+ no longer auto-configures the pdf.js worker (v4 did). Point it at
// the worker shipped with pdfjs-dist; Vite emits it as a hashed asset via `?url`.
pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;

const checkboxText = 'I HAVE READ AND AGREED TO THE END-USER LICENSE AGREEMENT SHOWN ABOVE';

interface FjAgreementsModalProps {
  agreeCallback: () => void;
  disagreeCallback: () => void;
  agreementHeaderImgSrc: string;
  agreementPdfPath: string;
  scrollToBottomText: React.ReactNode;
  understandLabelText: React.ReactNode;
  warnText: React.ReactNode;
  className: string;
}

interface FjAgreementsModalState {
  isScrollCompletete: boolean;
  isUnderstandChecked: boolean;
  understandColor: string;
  shouldShowWarning: boolean;
  pdfPages: number;
}

// Minimal shape of the react-pdf onLoadSuccess payload we consume.
interface PdfLoadSuccess {
  numPages: number;
}

class FjAgreementsModal extends React.Component<FjAgreementsModalProps, FjAgreementsModalState> {
  static defaultProps: FjAgreementsModalProps = {
    agreeCallback: noop,
    disagreeCallback: noop,
    agreementHeaderImgSrc: defaultHeaderImg,
    agreementPdfPath: '',
    scrollToBottomText:
      'You must scroll through to the bottom of the User Agreement and Privacy Policy to accept',
    understandLabelText: checkboxText,
    warnText:
      'By declining to accept the End User License Agreement, you will be signed out and cannot use the product.',
    className: '',
  };

  state: FjAgreementsModalState = {
    isScrollCompletete: false,
    isUnderstandChecked: false,
    understandColor: 'gray',
    shouldShowWarning: false,
    pdfPages: 1,
  };

  // FIXME: use refs here if we bump to React 16.3 API
  /* function used to check if there is a vertical scrollbar available to scroll */
  checkIfScroll = (): void => {
    const agreeScrollBox = document.getElementById('agreementText');
    const hasVerticalScrollbar = agreeScrollBox!.scrollHeight > agreeScrollBox!.clientHeight;
    if (!hasVerticalScrollbar) {
      this.setState({ isScrollCompletete: true });
    }
  };

  hideWarn = (): void => {
    this.setState({ shouldShowWarning: false });
  };

  /* function used to check if the user has scrolled all the way through the agreement text */
  scrollingAgreement = (scrollbar: HTMLElement): void => {
    if (scrollbar.offsetHeight + scrollbar.scrollTop >= scrollbar.scrollHeight) {
      this.setState({ isScrollCompletete: true });
    }
  };

  showWarn = (): void => {
    this.setState({ shouldShowWarning: true });
  };

  understandAction = (): void => {
    const fjInactiveTextColor = 'gray';
    const fjActiveTextColor = 'black';

    let { understandColor: currentUnderstandColor } = this.state;
    if (currentUnderstandColor === fjInactiveTextColor) {
      currentUnderstandColor = fjActiveTextColor;
    } else {
      currentUnderstandColor = fjInactiveTextColor;
    }
    this.setState((state) => ({
      isUnderstandChecked: !state.isUnderstandChecked,
      understandColor: currentUnderstandColor,
    }));

    this.checkIfScroll();
  };

  renderWarning = (): React.ReactNode => {
    const { shouldShowWarning } = this.state;
    const { disagreeCallback, warnText } = this.props;
    if (!shouldShowWarning) {
      return null;
    }

    return (
      <FjModal
        dataTestId="warning-modal"
        cancel={{
          text: 'Return to Agreement',
          callback: this.hideWarn,
        }}
        accept={{
          text: 'Sign Out',
          disabled: false,
          callback: disagreeCallback,
        }}
        size="sm"
      >
        <Components.WarningText>{warnText}</Components.WarningText>
      </FjModal>
    );
  };

  onDocumentLoadSuccess = (document: PdfLoadSuccess): void => {
    const { numPages } = document;
    this.setState({
      pdfPages: numPages,
    });
  };

  renderPDFAgreement = (): React.ReactNode => {
    const { agreementPdfPath } = this.props;
    if (agreementPdfPath === '') {
      return null;
    }

    return (
      <Document
        file={agreementPdfPath}
        loading="Loading Agreement"
        onLoadSuccess={this.onDocumentLoadSuccess}
      >
        {Array.from(new Array(this.state.pdfPages), (_: unknown, index: number) => (
          <Page key={`page_${index + 1}`} pageNumber={index + 1} renderAnnotationLayer={false} />
        ))}
      </Document>
    );
  };

  render(): React.ReactNode {
    const { isScrollCompletete, isUnderstandChecked, understandColor } = this.state;
    const {
      agreeCallback,
      className,
      agreementHeaderImgSrc,
      scrollToBottomText,
      understandLabelText,
    } = this.props;
    return (
      <FjModal
        blocking
        isDraggable={false}
        cancel={{
          text: 'I Disagree',
          callback: this.showWarn,
        }}
        accept={{
          text: 'I Agree',
          disabled: !(isScrollCompletete && isUnderstandChecked),
          callback: agreeCallback,
        }}
        size="xxl"
        className={className}
      >
        <Components.Wrapper>
          <Components.ProductHeaderImg src={agreementHeaderImgSrc} alt="agreement product header" />
          <Components.AgreementTextBox
            id="agreementText"
            onScroll={({ target }) => this.scrollingAgreement(target as HTMLElement)}
          >
            <Components.AgreementTextContainer>
              {this.renderPDFAgreement()}
            </Components.AgreementTextContainer>
          </Components.AgreementTextBox>
          <Components.ScrollToBottomText>{scrollToBottomText}</Components.ScrollToBottomText>
          <Components.UnderstandContainer>
            <FjCheckbox
              dataTestId="accept-eula-checkbox"
              onChange={this.understandAction}
              checked={isUnderstandChecked}
              label={
                <Components.UnderstandText color={understandColor}>
                  {understandLabelText}
                </Components.UnderstandText>
              }
            />
          </Components.UnderstandContainer>
          {this.renderWarning()}
        </Components.Wrapper>
      </FjModal>
    );
  }
}

export default FjAgreementsModal;
